<template>
  <div class="redeem-modal-container my-3">
    <p class="text-description-success">You have received {{ successfulCount }} new MCs.</p>

    <!-- Loop through different item types -->
    <div
      v-for="itemType in itemTypes"
      v-show="itemType.hasItems"
      :key="itemType.key"
      class="item-list-container"
      :class="itemType.containerClass"
    >
      <!-- Alert Section -->
      <b-alert
        v-if="itemType.alert"
        show
        :variant="itemType.alert.variant"
        class="alert-notification"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          :fill="itemType.alert.iconColor"
          class="alert-icon"
        >
          <path :d="itemType.alert.iconPath" />
        </svg>
        {{ itemType.alert.message }}
      </b-alert>

      <!-- Items Grid -->
      <div class="items-grid">
        <div
          v-for="(item, index) in itemType.validItems"
          :key="getItemKey(item, index)"
          class="item-card"
        >
          <li class="item-content">
            <base-presigned-image
              class="item-image"
              fluid-grow
              :file-id="getItemImage(item).id"
              :default-src="getItemImage(item).url"
            />
            <div class="item-text-content">
              <p class="item-name">{{ getItemName(item) }}</p>

              <b-badge
                v-if="itemType.key === 'successful'"
                class="item-badge"
                pill
                variant="secondary"
                >{{ getItemBadgeText(item) }}</b-badge
              >
            </div>
          </li>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BasePresignedImage from '@/components/base-components/BasePresignedImage.vue';
import defaultCoverImage from '../../assets/default-lu-cover.jpg';

export default {
  name: 'RedeemResultList',
  components: {
    BasePresignedImage
  },
  props: {
    stack: {
      type: Object,
      default: () => ({}),
      validator: (stack) => {
        return typeof stack === 'object' && stack !== null;
      }
    }
  },
  data() {
    return {
      defaultCoverImage: defaultCoverImage
    };
  },
  computed: {
    // Counts
    successfulCount() {
      return this.stack.successful?.length || 0;
    },

    // Configuration for different item types
    itemTypes() {
      return [
        {
          key: 'successful',
          hasItems: (this.stack.successful || []).length > 0,
          validItems: this.stack.successful || [],
          containerClass: 'success-list',
          alert: null // No alert for successful items
        },
        {
          key: 'alreadyApplied',
          hasItems: (this.stack.alreadyApplied || []).length > 0,
          validItems: this.stack.alreadyApplied || [],
          containerClass: 'warning-list',
          alert: {
            variant: 'warning',
            message:
              'The quota of your purchased MCs will remain the same and cannot be increased via redeem code.',
            iconColor: 'rgba(130,66,36,1)',
            iconPath:
              'M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM11 7H13V9H11V7ZM11 11H13V17H11V11Z'
          }
        },
        {
          key: 'roleRestricted',
          hasItems: (this.stack.roleRestricted || []).length > 0,
          validItems: this.stack.roleRestricted || [],
          containerClass: 'danger-list',
          alert: {
            variant: 'danger',
            message: 'Cannot access the MCs due to your role as a designer or assessor.',
            iconColor: 'rgba(132,30,48,1)',
            iconPath:
              'M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z'
          }
        }
      ];
    }
  },
  methods: {
    getItemKey(item, index) {
      return item.learningUnit?.id || `item-${index}`;
    },

    getItemImage(item) {
      const mediaDetail = item.learningUnit?.media_detail;
      const hasMediaDetail = Array.isArray(mediaDetail) && mediaDetail.length > 0;

      return hasMediaDetail
        ? mediaDetail[0]
        : {
            id: null,
            url: this.defaultCoverImage
          };
    },

    getItemName(item) {
      return item.learningUnit?.name || '4lifelonglearning MC';
    },

    getItemBadgeText(item) {
      const mode = item.application?.mode;

      const MODE_LABELS = {
        VERIFY: 'VERIFY',
        LEARN_VERIFY: 'LEARN + VERIFY'
      };

      return MODE_LABELS[mode];
    }
  }
};
</script>

<style lang="scss" scoped>
.redeem-modal-container {
  display: grid;
  gap: 16px;
}

.text-description-success {
  text-align: center;
  margin-bottom: 0;
}

.item-list-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  max-height: 280px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid #d1d2db;
  border-radius: 8px;
}

.items-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.item-card {
  list-style: none;
}

.item-content {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
}

.item-image {
  width: 100%;
  max-width: 120px;
  max-height: 75px;
  border-radius: 10.4px;
  object-fit: cover;
}

.item-image-placeholder {
  width: 120px;
  height: 75px;
  background-color: #d1d2db;
  border-radius: 10.4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 12px;
}

.item-name {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Max 2 lines */
  line-clamp: 2; /* Standard property for compatibility */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-text-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .item-badge {
    width: fit-content;
    height: auto;
    color: #7a7b97;
    background-color: #f4f5fd;
    padding: 2px 10px;
  }
}

.alert-notification {
  display: grid;
  gap: 12px;
  grid-template-columns: auto 1fr;
  align-items: center;
  margin-bottom: 0;
}

.alert-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
</style>
